<template>
  <div class="row g-4">
    <div class="col-12">
      <router-link to="/work" class="btn btn-primary btn-no-r">
        <span><BaseIcon name="active" /> {{ $t("work.dashboard") }}</span>
      </router-link>
      <router-link
        to="/work/tracking/history"
        class="btn btn-no-l btn-secondary"
      >
        <span><BaseIcon name="timer" /> {{ $t("work.timeTracking") }}</span>
      </router-link>
    </div>
    <div class="col-lg-7">
      <div class="row">
        <div class="col-12">
          <div class="card p-4">
            <template v-if="data.active?.length > 0">
              <div v-for="row in data.active" :key="row.id">
                <div class="d-flex justify-content-between">
                  <h6>Active Timer</h6>
                  <span class="badge badge-outline">{{ row.bucket }}</span>
                </div>
                <div class="timer-display mt-2" :key="timeKey">
                  {{ formatTime(elapsedTime(row.started_at), true) }}
                </div>
                <div class="small-muted">
                  <template v-if="row.is_automatic">
                    Automatic tracking from ActivityWatch
                  </template>
                  <template v-else>
                    {{ row.name ?? "Not categorized" }}
                  </template>
                </div>
                <div class="mt-3 d-flex gap-2">
                  <button class="btn btn-primary btn-sm">
                    <i class="bi bi-pause"></i> Stop
                  </button>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="d-flex justify-content-between">
                <h6>Active Timer</h6>
                <span class="badge badge-outline">No active timer</span>
              </div>
              <div class="px-4">
                <Vue3Lottie
                  :animationData="emptyJSON"
                  :height="128"
                  :width="300"
                />
              </div>
            </template>
          </div>
        </div>
      </div>
      <div class="g-4 mt-3">
        <div class="card p-4">
          <h5 class="mb-3">My Tasks</h5>
          <table class="table align-middle">
            <thead>
              <tr>
                <th>Task</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Due</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Update product inventory</td>
                <td><span class="badge bg-warning">In Progress</span></td>
                <td><span class="badge bg-danger">High</span></td>
                <td>Today</td>
              </tr>
              <tr>
                <td>Review supplier requests</td>
                <td><span class="badge bg-secondary">Pending</span></td>
                <td><span class="badge bg-warning">Medium</span></td>
                <td>Tomorrow</td>
              </tr>
              <tr>
                <td>Fix checkout bug</td>
                <td><span class="badge bg-warning">In Progress</span></td>
                <td><span class="badge bg-danger">High</span></td>
                <td>Today</td>
              </tr>
              <tr>
                <td>Upload product images</td>
                <td><span class="badge bg-success">Completed</span></td>
                <td><span class="badge bg-secondary">Low</span></td>
                <td>Yesterday</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div class="col-lg-5">
      <div class="card p-4 h-100">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="mb-0">Work Analytics & Tracked Activity</h6>
          <span class="small-muted">This Week</span>
        </div>
        <div class="row mb-3">
          <div class="col-md-4">
            <div class="text-muted small">Total Worked</div>
            <div class="fs-4 fw-semibold text-success">28h 15m</div>
          </div>
          <div class="col-md-4">
            <div class="text-muted small">Manual Tracking</div>
            <div class="fs-5 fw-semibold">21h 30m</div>
          </div>
          <div class="col-md-4">
            <div class="text-muted small">Warehouse Auto</div>
            <div class="fs-5 fw-semibold">
              {{ formatTime(totalTrackedTime) }}
            </div>
          </div>
        </div>
        <div class="progress mt-3" style="height: 8px">
          <div class="progress-bar bg-success" style="width: 70%"></div>
        </div>
        <div class="small-muted mt-2">12h remaining to weekly goal</div>
        <hr class="my-4" />
        <div class="row">
          <div class="col-md-6 mt-4">
            <div class="max-w-16rem mx-auto">
              <div class="day-labels mb-1">
                <span>M</span><span>T</span><span>W</span><span>T</span>
                <span>F</span><span>S</span><span>S</span>
              </div>
              <div class="activity-grid mb-3">
                <div v-for="week in last4Weeks" :key="week[0]" class="week-row">
                  <div
                    v-for="day in week"
                    :key="day"
                    :title="day"
                    class="heat-cell"
                    :class="'level-' + getHeatLevel(day)"
                  ></div>
                </div>
              </div>
            </div>
            <h6 class="mt-3 mb-3">Recent Activity</h6>
            <div class="small">
              <div class="d-flex mb-2">
                <div class="me-2 text-muted">•</div>
                <div>
                  <div>Tracked 1h on "Checkout Fix"</div>
                  <div class="small-muted">10 minutes ago</div>
                </div>
              </div>
              <div class="d-flex mb-2">
                <div class="me-2 text-muted">•</div>
                <div>
                  <div>Warehouse picking session recorded</div>
                  <div class="small-muted">25 minutes ago</div>
                </div>
              </div>
              <div class="d-flex mb-2">
                <div class="me-2 text-muted">•</div>
                <div>
                  <div>Completed "Upload product images"</div>
                  <div class="small-muted">1 hour ago</div>
                </div>
              </div>
              <div class="d-flex">
                <div class="me-2 text-muted">•</div>
                <div>
                  <div>Started "Inventory Update"</div>
                  <div class="small-muted">2 hours ago</div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <h6 class="mb-3">Warehouse Work</h6>

            <div
              class="mb-3"
              v-for="(time, category) in data.tracked_times.categories"
              :key="category"
            >
              <div class="d-flex justify-content-between small">
                <span>
                  <i
                    :class="{
                      'bi bi-box': category === 'picking',
                      'bi bi-box-seam': category === 'packing',
                      'bi bi-arrow-counterclockwise': category === 'returning',
                      'bi bi-box-arrow-in-down': category === 'sourcing',
                    }"
                  ></i>
                  {{ category.charAt(0).toUpperCase() + category.slice(1) }}
                </span>
                <span>{{ formatTime(time) }}</span>
              </div>
              <div class="progress" style="height: 6px">
                <div
                  class="progress-bar"
                  :class="{
                    'bg-success': category === 'packing',
                    'bg-warning': category === 'returning',
                    'bg-info': category === 'sourcing',
                  }"
                  :style="{ width: (time / totalTrackedTime) * 100 + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useStore } from "vuex";
import http from "@/modules/http";
import BaseIcon from "../../../components/icons/BaseIcon.vue";
import emptyJSON from "../../../assets/animations/emptyNotification.json";
import { Vue3Lottie } from "vue3-lottie";
export default {
  name: "MyWork",
  components: { BaseIcon, Vue3Lottie },
  created() {},
  data() {
    return {
      store: useStore(),
      data: undefined,
      timeKey: 0,
      emptyJSON: emptyJSON,
    };
  },
  computed: {
    totalTrackedTime() {
      if (!this.data?.tracked_times?.categories) return 1;
      return Object.values(this.data.tracked_times.categories).reduce(
        (sum, t) => sum + t,
        0
      );
    },
    last4Weeks() {
      const weeks = [];
      const today = new Date();

      // Find Monday of current week
      const dayOfWeek = today.getDay(); // Sunday = 0, Monday = 1
      const diffToMonday = (dayOfWeek + 6) % 7; // how many days back to Monday
      const currentMonday = new Date(today);
      currentMonday.setDate(today.getDate() - diffToMonday);

      for (let w = 0; w < 4; w++) {
        const week = [];
        for (let d = 0; d < 7; d++) {
          const day = new Date(currentMonday);
          day.setDate(currentMonday.getDate() - w * 7 + d); // previous weeks
          const yyyy = day.getFullYear();
          const mm = String(day.getMonth() + 1).padStart(2, "0");
          const dd = String(day.getDate()).padStart(2, "0");
          week.push(`${yyyy}-${mm}-${dd}`);
        }
        weeks.push(week);
      }

      return weeks;
    },
    maxTrackedSeconds() {
      const days = this.data?.tracked_times?.days;
      if (!days) return 1;
      return Math.max(...Object.values(days).map((v) => parseFloat(v)));
    },
    warehouses() {
      return this.store.state.warehouses;
    },
    path() {
      return this.$route.path;
    },
    sessionId() {
      return this.store.state.sessionId;
    },
    filters() {
      return this.store.state.filters?.buckets;
    },
  },
  mounted() {
    this.loadData();
    setInterval(() => this.timeKey++, 1010);
  },
  methods: {
    getHeatLevel(date) {
      const seconds = parseFloat(this.data?.tracked_times?.days?.[date] || 0);
      const max = this.maxTrackedSeconds || 1;
      let level = Math.ceil((seconds / max) * 6);
      if (level > 6) level = 6;
      return level; // 0–6
    },
    loadData() {
      http.fetch("/work/tasks/my-tasks").then((data) => {
        this.data = data;
      });
    },
    elapsedTime(startDate) {
      const elapsedMilliseconds =
        new Date().getTime() - new Date(startDate).getTime();
      return Math.floor(elapsedMilliseconds / 1000);
    },
    formatTime(seconds, secunds = false) {
      if (seconds >= 60) {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (secunds) {
          const remainingSeconds = seconds % 60;
          return `${hours}:${minutes
            .toString()
            .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
        }
        return `${hours}:${minutes.toString().padStart(2, "0")}`;
      } else {
        return `${seconds} s`;
      }
    },
  },
};
</script>
<style scoped>
.timer-display {
  font-size: 34px;
  font-weight: 700;
  letter-spacing: 1px;
}
.activity-grid {
  width: 100%;
}

.week-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  margin-bottom: 6px;
}

.heat-cell {
  padding-top: 100%;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  transition: 0.2s;
  background: #ebedf0;
}

.heat-cell:hover {
  transform: scale(1.1);
}

.level-0 {
  background: #e5e7eb;
}
.level-1 {
  background: #d8bae6;
}
.level-2 {
  background: #c89adf;
}
.level-3 {
  background: #b460dc;
}
.level-4 {
  background: #8548a3;
  color: #fff;
}
.level-5 {
  background: #632c7e;
  color: #fff;
}
.level-6 {
  background: #4f136d;
  color: #fff;
}
.level-7 {
  background: #351c41;
  color: #fff;
}
.level-8 {
  background: #1d0f23;
  color: #fff;
}

.warehouse-badge {
  font-size: 12px;
  padding: 6px 8px;
  border-radius: 8px;
}
.small-muted {
  font-size: 13px;
  color: #6c757d;
}
.week-labels {
  display: grid;
  grid-template-rows: repeat(4, 1fr);
  gap: 6px;
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 4px;
}
.day-labels {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 4px;
}
</style>
